import { reactive, ref } from 'vue';
import ApexCharts from 'apexcharts';
import BaseSetupService from '@/setup/BaseSetupService';
import SalarySlipApiTempWorker from '@/api/SalarySlipApiTempWorker';

type Props = SetupProps<typeof DashboardTempWorkerSalaryCurrentYearCardSetupService.props>
type Emits = { (e: 'hide'): void }

export default class DashboardTempWorkerSalaryCurrentYearCardSetupService extends BaseSetupService<Props> {
	static readonly props = undefined;

	readonly _chart = ref<HTMLElement>();

	emits: Emits;

	private readonly state = reactive({
		dataLoaded: false,
		series: [] as ApexAxisChartSeries,
		chart: undefined as Undef<ApexCharts>,
	});

	get dataLoaded () { return this.state.dataLoaded;}

	constructor (emits: Emits) {
		super();
		this.emits = emits;
	}

	protected async onBeforeMountAsync () {
		await this.initChartAsync();
		this.state.dataLoaded = true;
	}

	protected onBeforeUnmount (): void {
		this.state.chart?.destroy();
	}


	async initChartAsync () {
		const { data } = await SalarySlipApiTempWorker.salaryCurrentYear();
		if (data.length === 0) {
			this.emits('hide');
			return;
		}

		this.state.series.push(
			{
				name: 'Rémunération horaire',
				data: data.map(x => x.ratio),
				type: 'area',
			},
			{
				name: 'Montant Brut',
				data: data.map(x => x.grossAmount),
				type: 'line',
			},
			{
				name: 'Heures travaillées',
				data: data.map(x => x.workedHour),
				type: 'column',
			},
		);

		const options= {
			series: this.state.series,
			chart: {
				type: 'line',
				height: '350px',
				toolbar: { show: false },
			},
			stroke: {
				width: 2,
				curve: 'smooth',
			},
			fill: { opacity: [0.2, 1, 0.5] },
			xaxis: {
				type: 'datetime',
				categories: data.map(x => x.timestamp),
				labels: {
					datetimeFormatter: {
						year: 'yyyy',
						month: 'MM/yy',
						day: 'dd/mm/yy',
						hour: 'HH:mm',
					},
				},
				tooltip: { formatter: (value: string) => new Date(value).toReadable('$MM/$YY') },
			},
			yaxis: [
				{
					seriesName: 'Rémunération horaire',
					labels: {
						style: {
							colors: 'rgb(0, 143, 251)',
							fontWeight: 600,
							fontFamily: 'Source Sans Pro',
							fontSize: '13px',
						},
						formatter: (value: string) => Number(value),
					},
				},
				{
					seriesName: 'Montant Brut',
					labels: {
						style: {
							colors: 'rgb(0, 227, 150)',
							fontWeight: 600,
							fontFamily: 'Source Sans Pro',
							fontSize: '13px',
						},
						formatter: (value: string) => Number(value),
					},
				},
				{
					seriesName: 'Heures travaillées',
					opposite: true,
					labels: {
						style: {
							colors: 'rgb(254, 176, 25)',
							fontWeight: 600,
							fontFamily: 'Source Sans Pro',
							fontSize: '13px',
						},
						formatter: (value: string) => `${value} h`,
					},
				},
			],
			tooltip: {
				x: { formatter: (value:string) => new Date(value).toReadable('$MM/$YY') },
				y: [
					{ formatter: (value:string) => Number(value) },
					{ formatter: (value: string) => Number(value) },
					{ formatter: (value: string) => `${value} h` },
				],
			},
			legend: {
				height: 40,
				offsetY: 10,
			},
			dataLabels: { enabled: false },
		};

		if (this._chart.value) {
			this.state.chart = new ApexCharts(this._chart.value, options);
			this.state.chart.render();
		}
	}
}
